import React, { Component } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Segment } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import Icon from '../../../elementals/icon'
import styles from '../styles.scss'

const cx = classnames.bind(styles)

export const BADGE_ORDER_PRIORITY = {
  FIRST: 1,
  SECOND: 2,
  THIRD: 3,
  FOURTH: 4,
  FIFTH: 5,
  SIXTH: 6,
  SEVENTH: 7,
  EIGHTH: 8,
  // these remain for backward compatibility, but also useful for approximate priorities
  HIGH: 1,
  MEDIUM: 4,
  LOW: 7,
}

export const BADGE_MODE = {
  EXPANDED: 'expanded',
  CONDENSED: 'condensed',
  V2: 'v2',
}

export const CondensedBadge = ({ elmId, title, iconName, icon }) => (
  <div className={cx('badge-condensed')} data-elm-id={`${elmId}_badge`}>
    {React.isValidElement(icon) ? (
      icon
    ) : (
      <Icon
        iconStyle='light'
        size='large'
        className={cx('icon')}
        name={iconName}
      />
    )}{' '}
    <label data-elm-id={`${elmId}_label`}>{title}</label>
  </div>
)

CondensedBadge.propTypes = {
  elmId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  iconName: PropTypes.string,
  icon: PropTypes.string,
}

export default class ListingBadge extends Component {
  static renderPriority = BADGE_ORDER_PRIORITY.MEDIUM

  static titleIcon = 'check'

  static title = ''

  static textIcon = 'check'

  static summary = 'Yes'

  static contentPositive = true

  static content = ''

  static shouldBeHoisted = false

  static propTypes = {
    listing: PropTypes.object.isRequired, // eslint-disable-line
    eventSubscriber: PropTypes.func,
    mode: PropTypes.oneOf(Object.values(BADGE_MODE)),
  }

  static defaultProps = {
    eventSubscriber: _.noop,
    mode: BADGE_MODE.EXPANDED,
  }

  state = { showContent: false }

  /**
   * Decides whether this badge considers the current listing to have
   * everything it needs either to calculate the badge content. This takes
   * the place of what a lot of the guard conditions in pal already do.
   *
   * For example, a lot of v2 data right doesn't have bidding information, so if any badge has any bidding information
   * embedded, we can't render that right now.
   *
   * @param {*} listing
   * @param {*} badgeMode -- specifies which mode you are asking about
   * @returns Boolean
   */
  static shouldBeIncluded(listing, badgeMode) {
    // do not include for any modes - by default
    return false
  }

  /**
   * Only to be used if different than standard listing icon.  Don't want to have to do this, but we're just not 100%
   * consistent yet between the different usages.
   */
  static condensedTitleIcon = ''

  /**
   * Only to be used if different than standard listing icon.  Don't want to have to do this, but we're just not 100%
   * consistent yet between the different usages.
   */
  static condensedTitle = ''

  /**
   * Some badges require hooking into the surrounding execution context to conduct some further action -- open a modal,
   * trigger an action etc. These have to be manually rendered by the user. In order for the caller to know when to take
   * action they subscribe to unique events originating from a listing badge, and react accordingly.
   */
  emitEvent(evtRef, e) {
    this.props.eventSubscriber(evtRef, e || null)
  }

  toggleContent = () => {
    this.setState({
      showContent: !this.state.showContent,
    })
  }

  render() {
    const showContent = this.state.showContent
    const content = this.constructor.content || this.content
    const summary = this.summary || this.constructor.summary
    const contentPositive =
      this.contentPositive || this.constructor.contentPositive

    const { titleIcon, title, condensedTitleIcon, condensedTitle } =
      this.constructor

    const elmId = _.snakeCase(title)

    return this.props.mode === BADGE_MODE.CONDENSED ? (
      <CondensedBadge
        elmId={elmId}
        title={condensedTitle || title || summary}
        iconName={condensedTitleIcon || titleIcon}
        icon={titleIcon}
      />
    ) : (
      <Segment className={cx('badge')}>
        <div
          className={cx('u-display-f', 'u-pointer-cursor')}
          data-elm-id={`${elmId}_badge_header`}
          onClick={this.toggleContent}
        >
          <div
            className={cx(
              'u-display-f',
              'u-display-f-between',
              'u-stretch',
              'badge-title'
            )}
          >
            <div data-elm-id={`${elmId}_title`}>
              <Icon
                iconStyle='light'
                className={cx('title-icon')}
                name={titleIcon}
              />{' '}
              {title}
            </div>
            <div data-elm-id={`${elmId}_summary`}>
              <strong
                className={cx(
                  contentPositive ? 'color-success' : 'color-warning'
                )}
              >
                {' '}
                {summary}{' '}
              </strong>
            </div>
          </div>
          {!!content && (
            <div className={cx('u-display-f-end', 'icon-container')}>
              <Icon
                name='angle-down'
                className={cx('toggle', { 'icon-up': showContent })}
                iconStyle='light'
                size='large'
              />
            </div>
          )}
        </div>
        <div data-elm-id={`${elmId}_badge_content`}>
          <div
            className={cx('badge-hidden-content', 'scroll-up', {
              hidden: !showContent,
            })}
          >
            {content}
          </div>
        </div>
      </Segment>
    )
  }
}
